import { useCallback, useMemo, useState } from 'react'
import { Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import {
  createTxtifyEditingSession,
  TXTIFY_ANALYTICS_TOOLS,
} from '@/shared/analytics/tool-operations'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

type SeoLimitFieldKey = 'title' | 'description' | 'snippet' | 'social'
type LimitState = 'empty' | 'under' | 'good' | 'over'

interface SeoLimitRange {
  key: SeoLimitFieldKey
  min: number
  max: number
}

interface SeoLimitResult extends SeoLimitRange {
  label: string
  placeholder: string
  text: string
  characters: number
  state: LimitState
}

interface SegmenterConstructor {
  new (
    locales?: string | string[],
    options?: { granularity: 'grapheme' },
  ): {
    segment(text: string): Iterable<unknown>
  }
}

const RANGES: SeoLimitRange[] = [
  { key: 'title', min: 45, max: 60 },
  { key: 'description', min: 120, max: 160 },
  { key: 'snippet', min: 140, max: 220 },
  { key: 'social', min: 70, max: 110 },
]

const EMPTY_VALUES: Record<SeoLimitFieldKey, string> = {
  title: '',
  description: '',
  snippet: '',
  social: '',
}

function graphemeCount(text: string): number {
  const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter
  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' })
    return [...segmenter.segment(text)].length
  }
  return [...text].length
}

function stateFor(characters: number, min: number, max: number): LimitState {
  if (characters === 0) return 'empty'
  if (characters < min) return 'under'
  if (characters > max) return 'over'
  return 'good'
}

function fieldLabels(t: Translations): Record<SeoLimitFieldKey, string> {
  return {
    title: t.seoLimits.fields.title,
    description: t.seoLimits.fields.description,
    snippet: t.seoLimits.fields.snippet,
    social: t.seoLimits.fields.social,
  }
}

function fieldPlaceholders(t: Translations): Record<SeoLimitFieldKey, string> {
  return {
    title: t.seoLimits.placeholders.title,
    description: t.seoLimits.placeholders.description,
    snippet: t.seoLimits.placeholders.snippet,
    social: t.seoLimits.placeholders.social,
  }
}

function analyzeValues(
  values: Record<SeoLimitFieldKey, string>,
  t: Translations,
): SeoLimitResult[] {
  const labels = fieldLabels(t)
  const placeholders = fieldPlaceholders(t)

  return RANGES.map((range) => {
    const text = values[range.key]
    const characters = graphemeCount(text)
    return {
      ...range,
      label: labels[range.key],
      placeholder: placeholders[range.key],
      text,
      characters,
      state: stateFor(characters, range.min, range.max),
    }
  })
}

function stateLabel(state: LimitState, t: Translations): string {
  switch (state) {
    case 'empty':
      return t.seoLimits.states.empty
    case 'under':
      return t.seoLimits.states.under
    case 'good':
      return t.seoLimits.states.good
    case 'over':
      return t.seoLimits.states.over
  }
}

function stateClass(state: LimitState): string {
  switch (state) {
    case 'empty':
      return 'border-muted bg-muted/45 text-muted-foreground'
    case 'under':
      return 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100'
    case 'good':
      return 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100'
    case 'over':
      return 'border-red-300 bg-red-50 text-red-950 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100'
  }
}

function progressClass(state: LimitState): string {
  switch (state) {
    case 'empty':
      return 'bg-muted-foreground/25'
    case 'under':
      return 'bg-amber-500'
    case 'good':
      return 'bg-emerald-500'
    case 'over':
      return 'bg-red-500'
  }
}

export function SeoLimitsWorkspace() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [values, setValues] = useState<Record<SeoLimitFieldKey, string>>(EMPTY_VALUES)
  const [analyticsSession] = useState(() => (
    createTxtifyEditingSession(TXTIFY_ANALYTICS_TOOLS.seoLimits)
  ))
  const results = useMemo(() => analyzeValues(values, t), [t, values])
  const totalCharacters = results.reduce((sum, result) => sum + result.characters, 0)
  const filledCount = results.filter((result) => result.characters > 0).length
  const goodCount = results.filter((result) => result.state === 'good').length

  const handleChange = useCallback(
    (key: SeoLimitFieldKey, next: string) => {
      const nextValues = { ...values, [key]: next }

      analyticsSession.run(
        Object.values(nextValues).some((value) => value.length > 0),
        () => setValues(nextValues),
      )
    },
    [analyticsSession, values],
  )

  return (
    <>
      <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {results.map((result) => {
            const progress =
              result.characters > 0
                ? Math.min(100, Math.round((result.characters / result.max) * 100))
                : 0

            return (
              <Card key={result.key} className="overflow-hidden shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
                  <div>
                    <label
                      htmlFor={`seo-limit-${result.key}`}
                      className="text-sm font-medium tracking-normal"
                    >
                      {result.label}
                    </label>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t.seoLimits.recommended}: {result.min}-{result.max}{' '}
                      {t.seoLimits.charactersShort}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'inline-flex rounded-md border px-2.5 py-1 text-xs font-medium',
                      stateClass(result.state),
                    )}
                  >
                    {stateLabel(result.state, t)}
                  </span>
                </div>

                <textarea
                  id={`seo-limit-${result.key}`}
                  value={result.text}
                  onChange={(event) => handleChange(result.key, event.target.value)}
                  placeholder={result.placeholder}
                  className={cn(
                    'min-h-28 w-full resize-y border-0 bg-card p-4',
                    'text-[15px] leading-7 text-foreground placeholder:text-muted-foreground/55',
                    'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
                  )}
                  aria-label={result.label}
                />

                <div className="border-t px-4 py-3">
                  <div className="flex justify-between gap-4 text-xs text-muted-foreground">
                    <span>
                      {result.characters.toLocaleString(localeCode)}{' '}
                      {t.seoLimits.charactersShort}
                    </span>
                    <span>
                      {result.min}-{result.max}
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', progressClass(result.state))}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </Card>
            )
          })}
        </div>

        <aside className="space-y-4">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.seoLimits.summary}
              </div>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-muted-foreground">{t.seoLimits.totalCharacters}</dt>
                  <dd className="font-semibold tabular-nums">
                    {totalCharacters.toLocaleString(localeCode)}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-muted-foreground">{t.seoLimits.filledFields}</dt>
                  <dd className="font-semibold tabular-nums">
                    {filledCount.toLocaleString(localeCode)} / {results.length}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-muted-foreground">{t.seoLimits.goodFields}</dt>
                  <dd className="font-semibold tabular-nums">
                    {goodCount.toLocaleString(localeCode)} / {results.length}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card className="border-dashed shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.seoLimits.noteTitle}</div>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t.seoLimits.note}
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.seoLimits.rangeTitle}
              </div>
              <div className="mt-3 space-y-2">
                {results.map((result) => (
                  <div
                    key={result.key}
                    className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2 text-sm"
                  >
                    <span className="text-muted-foreground">{result.label}</span>
                    <span className="font-medium tabular-nums">
                      {result.min}-{result.max}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </aside>
      </section>
    </>
  )
}

export function SeoLimitsPage() {
  return (
    <ToolPageShell toolKey="seoLimits">
      <SeoLimitsWorkspace />
    </ToolPageShell>
  )
}
